import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { InvestigateSplitButton } from "../shared/InvestigateSplitButton";
import { panelActionRowClass } from "../shared/panelActionStyles";
import { formatRelativeTime } from "@/components/missions/MissionRow";
import { ShareButton } from "@/components/ui/side-panel";
import type { ScreeningDocument } from "@/services/api/guardiansv2/types";
import { AlertScreeningThread } from "../alerts/AlertScreeningThread";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";
import type { FullAuditProgress } from "../auditLaunch/guardiansAuditLaunchStore";
import { ContextRow } from "../shared/ContextRow";
import { EntityLink } from "../shared/EntityLink";
import { screeningListTitle } from "./screeningPresentation";

export interface ScreeningInspectorProps {
  screening: ScreeningDocument | null;
  loading: boolean;
  shareUrl?: string;
  onClose: () => void;
  scopeLabels: ScopeLabelCache;
  onOpenAlert?: (alertId: string) => void;
  onInvestigate?: (userFocus?: string) => void;
  investigateProgress?: FullAuditProgress;
}

function formatWorkloadJson(serialized: string): string {
  try {
    return JSON.stringify(JSON.parse(serialized), null, 2);
  } catch {
    return serialized;
  }
}

export function ScreeningInspector({
  screening,
  loading,
  shareUrl,
  onClose,
  scopeLabels,
  onOpenAlert,
  onInvestigate,
  investigateProgress,
}: ScreeningInspectorProps) {
  const investigating = investigateProgress?.active ?? false;

  if (loading) {
    return (
      <div className="flex h-full flex-col p-6" data-testid="screening-inspector">
        <p className="text-sm text-slate-400">Loading screening…</p>
      </div>
    );
  }

  if (!screening) {
    return (
      <div className="flex h-full flex-col p-6" data-testid="screening-inspector">
        <p className="text-sm text-slate-400">Screening not found.</p>
      </div>
    );
  }

  const title = screeningListTitle(screening);
  const summary = screening.outcome.summary?.trim();
  const workload = screening.workload_context?.serialized_json;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="screening-inspector">
      <header className="flex shrink-0 items-start gap-3 border-b border-white/5 px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-slate-100">{title}</h2>
          <p className="mt-1 text-xs text-slate-400 tabular-nums">
            {formatRelativeTime(screening.created_at)}
            {screening.catalogue_key ? ` · ${screening.catalogue_key}` : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {shareUrl ? <ShareButton url={shareUrl} /> : null}
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-slate-200"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {screening.subject ? (
          <section className="space-y-3" data-testid="screening-inspector-context">
            <ContextRow label="Subject">
              <EntityLink
                subject={screening.subject}
                scope={screening.scope ?? {}}
                scopeLabels={scopeLabels}
              />
            </ContextRow>
          </section>
        ) : null}

        {summary ? (
          <p className="mt-4 text-sm text-slate-300">{summary}</p>
        ) : null}

        {screening.alert_id && onOpenAlert ? (
          <button
            type="button"
            className="mt-4 text-sm text-indigo-300 hover:text-indigo-200"
            onClick={() => onOpenAlert(screening.alert_id!)}
            data-testid="screening-open-alert"
          >
            View linked alert
          </button>
        ) : null}

        <div className="mt-6">
          <AlertScreeningThread
            screening={screening}
            loading={false}
            scopeLabels={scopeLabels}
            showContextHeader={false}
          />
        </div>

        {workload ? (
          <details className="mt-8 border-t border-white/5 pt-4" data-testid="screening-workload-json">
            <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-300">
              Screening context (JSON)
            </summary>
            {screening.workload_context?.emergency_minimal ? (
              <p className="mt-2 text-xs text-amber-300/90">
                Context was degraded to fit size limits; some tool output may be summarized.
              </p>
            ) : null}
            <pre className="mt-2 max-h-96 overflow-auto rounded-xl bg-slate-900/60 p-3 text-[11px] leading-relaxed text-slate-300 whitespace-pre-wrap break-words">
              {formatWorkloadJson(workload)}
            </pre>
          </details>
        ) : (
          <p className="mt-8 border-t border-white/5 pt-4 text-xs text-slate-500">
            Screening context was not captured for this run.
          </p>
        )}

        {onInvestigate ? (
          <div className="mt-6 border-t border-white/5 pt-4">
            <div className={panelActionRowClass} data-testid="screening-investigate">
              <InvestigateSplitButton
                label="Investigate"
                disabled={investigating}
                progressPercent={investigateProgress?.percent ?? 0}
                onInvestigate={onInvestigate}
                testIdPrefix="screening-investigate"
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
